/* eslint-disable react/jsx-no-undef */
import { lazy, Suspense } from 'react'
import { PhotoProvider } from 'react-photo-view'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'

import { usePermission } from './hooks/usePermission'

import CustomGraphy from './components/CustomGraphy'
import CustomView from './components/CustomView'
import DashboardHeader from './components/DashboardHeader'
import Menu from './components/Menu'
import Template from './templates'
import { PATHS } from './utils/paths'
import {
  PermissionAction,
  PermissionCheck,
  PermissionResource
} from './types/permissions.types'

import { AssetLocationProvider } from './context/AssetLocationContext'
import { DashboardProvider } from './context/DashboardContext'
import { GlobalInformationsProvider } from './context/GlobalInformationsContext'
import { GraphysDataProvider } from './context/graphysDataContext'
import { isTokenExpired } from './utils/token'
import { InspectionProvider } from './context/InspectionContext'
import { LoadingProvider } from './context/LoadingContext'
import { LoginProvider } from './context/LoginContext'
import { ManagementAnalysisProvider } from './context/ManagementAnalysisContext'
import { ParametrizationProvider } from './context/ParametrizationContext'
import { PrecificationProvider } from './context/PrecificationContext'
import { PreparationProvider } from './context/PreparationContext'
import { ProfileProvider } from './context/ProfileContext'
import { StepperProvider } from './context/StepperContext'
import { UsersProvider } from './context/usersContext'

import ConfirmCpf from './pages/Login/components/ConfirmCpf'
import ConfirmEmail from './pages/Login/components/ConfirmEmail'
import Integrations from './pages/Integrations'
import Checklist from './pages/Parametrization/Checklist'

const Login = lazy(() => import('@/pages/Login'))
const ForgotPassword = lazy(
  () => import('@/pages/Login/components/ForgotPassword')
)
const ChangePassword = lazy(
  () => import('@/pages/Login/components/ChangePassword')
)
const FullLogin = lazy(() => import('@/pages/Login/components/FullLogin'))
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'))

const Home = lazy(() => import('@/pages/Home'))
const Precification = lazy(() => import('@/pages/Precification'))
const NewPrecification = lazy(
  () => import('@/pages/Precification/components/new/NewPrecification')
)
const Breakdowns = lazy(() => import('@/pages/Parametrization/Breakdowns'))
const NewBreakdown = lazy(
  () => import('@/pages/Parametrization/components/new/NewBreakdowns')
)
const EditBreakdown = lazy(
  () => import('@/pages/Parametrization/components/edit/EditBreakdowns')
)
const CategoriaVistoria = lazy(
  () => import('@/pages/Parametrization/CategoriaVistoria')
)
const NewCategoriaVistoria = lazy(
  () => import('@/pages/Parametrization/components/new/NewCategoriaVistoria')
)
const EditCategoryInspection = lazy(
  () => import('@/pages/Parametrization/components/edit/EditCategoryInspection')
)
const Inspection = lazy(() => import('@/pages/Inspection'))
const NewSurvey = lazy(
  () => import('@/pages/Inspection/components/new/NewSurvey/NewSurvey')
)
const InspectionDetailWrapper = lazy(
  () => import('@/pages/Inspection/components/InspectionDetailWrapper')
)
const InspectionExecution = lazy(() => import('@/pages/Inspection/Execution'))
const Refund = lazy(() => import('@/pages/Refund'))
const NewRefund = lazy(() => import('@/pages/Refund/components/new/NewRefund'))
const Users = lazy(() => import('@/pages/Users'))
const NewUser = lazy(() => import('@/pages/Users/components/new/NewUser'))
const Maintenance = lazy(() => import('@/components/Maintenance'))
const Categories = lazy(() => import('@/pages/Parametrization/Categories'))
const NewCategory = lazy(
  () => import('@/pages/Parametrization/components/new/NewCategory')
)
const EditCategory = lazy(
  () => import('@/pages/Parametrization/components/edit/EditCategory')
)
const Products = lazy(() => import('@/pages/Parametrization/Products'))
const NewProduct = lazy(
  () => import('@/pages/Parametrization/components/new/NewProduct')
)
const EditProduct = lazy(
  () => import('@/pages/Parametrization/components/edit/EditProduct')
)
const Providers = lazy(() => import('@/pages/Parametrization/Providers'))
const NewProvider = lazy(
  () => import('@/pages/Parametrization/components/new/NewProvider')
)
const EditProvider = lazy(
  () => import('@/pages/Parametrization/components/edit/EditProvider')
)
const Services = lazy(() => import('@/pages/Parametrization/Services'))
const NewService = lazy(
  () => import('@/pages/Parametrization/components/new/NewService')
)
const EditServices = lazy(
  () => import('@/pages/Parametrization/components/edit/EditServices')
)
const SLA = lazy(() => import('@/pages/Parametrization/SLA'))
const NewSLA = lazy(
  () => import('@/pages/Parametrization/components/new/NewSLA')
)
const EditSLA = lazy(
  () => import('@/pages/Parametrization/components/edit/EditSLA')
)
const AppraisalCompany = lazy(
  () => import('@/pages/Parametrization/AppraisalCompany')
)
const NewCompany = lazy(
  () => import('@/pages/Parametrization/components/new/NewCompany')
)
const EditAppraisalCompany = lazy(
  () =>
    import('@/pages/Parametrization/components/edit/EditAppraisalCompany/EditAppraisalCompany')
)
const SerialItem = lazy(() => import('@/pages/Parametrization/SerialItem'))
const NewSerialItem = lazy(
  () => import('@/pages/Parametrization/components/new/NewSerialItem')
)
const EditSerialItem = lazy(
  () => import('@/pages/Parametrization/components/edit/EditSerialItem')
)
const Accessories = lazy(() => import('@/pages/Parametrization/Accessories'))
const InspectionParametrization = lazy(
  () => import('@/pages/Parametrization/Inspection')
)
const NewAccessorie = lazy(
  () => import('@/pages/Parametrization/components/new/NewAccessorie')
)
const EditAccessorie = lazy(
  () => import('@/pages/Parametrization/components/edit/EditAccessorie')
)
const Mark = lazy(() => import('@/pages/Parametrization/Mark'))
const EditMark = lazy(
  () => import('@/pages/Parametrization/components/edit/EditMark')
)
const NewMark = lazy(
  () => import('@/pages/Parametrization/components/new/NewMark')
)
const Files = lazy(() => import('@/pages/Files'))
const Preparation = lazy(() => import('@/pages/Preparation'))
const NewPreparation = lazy(
  () => import('@/pages/Preparation/components/new/NewPreparation')
)
const EditPreparation = lazy(
  () => import('@/pages/Preparation/components/edit/EditPreparation')
)
const AssetSummary = lazy(
  () => import('@/pages/Preparation/components/AssetSummaryl')
)
const Profile = lazy(() => import('@/pages/Profile'))
const Welcome = lazy(() => import('@/pages/Welcome'))
const Forbidden = lazy(() => import('@/pages/Forbidden'))
const AssetLocation = lazy(() => import('@/pages/AssetLocation'))
const AssetHistory = lazy(
  () => import('@/pages/AssetLocation/components/AssetHistory')
)
const ManagementAnalysis = lazy(() => import('@/pages/ManagementAnalysis'))
const EvaluationType = lazy(
  () => import('@/pages/Parametrization/EvaluationType')
)
const NewEvaluationType = lazy(
  () => import('@/pages/Parametrization/components/new/NewEvaluationType')
)
const EditEvaluationType = lazy(
  () => import('@/pages/Parametrization/components/edit/EditEvaluationType')
)
const InspectionPhotos = lazy(
  () => import('@/pages/Parametrization/InspectionPhotos')
)
const NewInspectionPhoto = lazy(
  () => import('@/pages/Parametrization/components/new/NewInspectionPhoto')
)
const EditInspectionPhoto = lazy(
  () => import('@/pages/Parametrization/components/edit/EditInspectionPhoto')
)
const PhotoSections = lazy(
  () => import('@/pages/Parametrization/PhotoSections')
)
const NewPhotoSection = lazy(
  () => import('@/pages/Parametrization/components/new/NewPhotoSection')
)
const EditPhotoSection = lazy(
  () => import('@/pages/Parametrization/components/edit/EditPhotoSection')
)
const PhotoFlow = lazy(() => import('@/pages/Parametrization/PhotoFlow'))
const NewPhotoFlow = lazy(
  () => import('@/pages/Parametrization/components/new/NewPhotoFlow')
)
const EditPhotoFlow = lazy(
  () =>
    import('@/pages/Parametrization/components/edit/EditPhotoFlow/EditPhotoFlow')
)

const LogisticRoutes = () => {
  const { HAS_PERMISSION, HAS_ALL_PERMISSIONS } = usePermission()
  const navigate = useNavigate()
  const location = useLocation()

  const handleReturnToTable = (fallbackPath: string) => {
    const previousPath = location.state?.from ?? null
    if (previousPath) return navigate(-1)

    navigate(fallbackPath)
  }
  // ID deve ter o mesmo valor da permissão do usuário retornado no login para ser exibido caso allowanonymous = false
  type RouteType = {
    resource: PermissionResource | string
    path: string
    component: JSX.Element
    allowanonymous?: boolean
    imgFixed?: boolean
    action?: PermissionAction
    extraPermissions?: PermissionCheck[]
  }

  const loginPath: RouteType[] = [
    { resource: 'dashboard', path: '/login', component: <FullLogin /> },
    {
      resource: 'esqueceusenha',
      path: '/esqueceu-senha',
      component: <ForgotPassword />
    },
    {
      resource: 'recuperarsenha',
      path: '/recuperar-senha',
      component: <ChangePassword />
    },
    {
      resource: 'confirmaralteracaoemail',
      path: '/confirmar-alteracao-email',
      component: <ConfirmEmail />
    },
    {
      resource: 'confirmaralteracaocpf',
      path: '/confirmar-alteracao-cpf',
      component: <ConfirmCpf />
    },
    {
      resource: 'politicaprivacidade',
      path: '/politica-privacidade',
      component: <PrivacyPolicy />,
      imgFixed: true
    }
  ]

  const systemPath: RouteType[] = [
    {
      resource: 'home',
      path: '/home',
      component: <Welcome />,
      allowanonymous: true
    },
    {
      resource: PermissionResource.DASHBOARD,
      path: '/dashboard',
      component: <Home />,
      allowanonymous: false
    },
    {
      resource: 'forbidden',
      path: '/forbidden',
      component: <Forbidden />,
      allowanonymous: true
    },
    {
      resource: PermissionResource.INSPECTION,
      path: `${PATHS.inspections.index}`,
      component: <Inspection />,
      allowanonymous: false,
      action: PermissionAction.READ
    },
    {
      resource: PermissionResource.INSPECTION,
      path: `${PATHS.inspections.new}`,
      component: (
        <NewSurvey
          returnToTable={() => navigate(`${PATHS.inspections.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.INSERT
    },
    {
      resource: PermissionResource.INSPECTION,
      path: `${PATHS.inspections.view(':id/:aba?')}`,
      component: (
        <InspectionDetailWrapper
          readonly
          returnToTable={() => navigate(`${PATHS.inspections.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.READ
    },
    {
      resource: PermissionResource.INSPECTION,
      path: `${PATHS.inspections.evaluate(':id/:aba?')}`,
      component: (
        <InspectionDetailWrapper
          returnToTable={() => navigate(`${PATHS.inspections.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.EVALUATE
    },
    {
      resource: PermissionResource.INSPECTION,
      path: `${PATHS.inspections.start(':id')}`,
      component: (
        <InspectionExecution
          returnToTable={() => navigate(`${PATHS.inspections.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.REFUND,
      path: `${PATHS.refund.index}`,
      component: <Refund />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.REFUND,
      path: `${PATHS.refund.new}`,
      component: (
        <NewRefund
          returnToTable={() => handleReturnToTable(`${PATHS.refund.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.REFUND,
      path: `${PATHS.refund.edit(':id')}`,
      component: (
        <NewRefund
          returnToTable={() => handleReturnToTable(`${PATHS.refund.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PRECIFICATION,
      path: `${PATHS.precification.index}`,
      component: <Precification />,
      allowanonymous: false,
      action: PermissionAction.READ
    },
    {
      resource: PermissionResource.PRECIFICATION,
      path: `${PATHS.precification.new}`,
      component: (
        <NewPrecification
          returnToTable={() => navigate(`${PATHS.precification.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.INSERT
    },
    {
      resource: PermissionResource.PRECIFICATION,
      path: `${PATHS.precification.view(':id/:aba?')}`,
      component: (
        <NewPrecification
          readonly
          returnToTable={() => navigate(`${PATHS.precification.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.READ
    },
    {
      resource: PermissionResource.PRECIFICATION,
      path: `${PATHS.precification.edit(':id/:aba?')}`,
      component: (
        <NewPrecification
          returnToTable={() => navigate(`${PATHS.precification.index}`)}
        />
      ),
      allowanonymous: false,
      action: PermissionAction.UPDATE
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.index}`,
      component: <Preparation />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.new}`,
      component: (
        <NewPreparation
          returnToTable={() =>
            handleReturnToTable(`${PATHS.preparation.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.view(':id')}`,
      component: (
        <EditPreparation
          readonly
          returnToTable={() =>
            handleReturnToTable(`${PATHS.preparation.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.edit(':id')}`,
      component: (
        <EditPreparation
          returnToTable={() =>
            handleReturnToTable(`${PATHS.preparation.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.complete(':id')}`,
      component: (
        <EditPreparation
          isComplete
          returnToTable={() =>
            handleReturnToTable(`${PATHS.preparation.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PREPARATION,
      path: `${PATHS.preparation.summary(':id')}`,
      component: (
        <AssetSummary
          returnToTable={() =>
            handleReturnToTable(`${PATHS.preparation.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PROFILE,
      path: '/profile',
      component: <Profile />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.USER,
      path: `${PATHS.users.index}`,
      component: <Users />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.USER,
      path: `${PATHS.users.new}`,
      component: (
        <NewUser
          isNew
          returnToTable={() => handleReturnToTable(`${PATHS.users.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.USER,
      path: `${PATHS.users.view(':id')}`,
      component: (
        <NewUser
          readonly
          returnToTable={() => handleReturnToTable(`${PATHS.users.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.USER,
      path: `${PATHS.users.edit(':id')}`,
      component: (
        <NewUser
          returnToTable={() => handleReturnToTable(`${PATHS.users.index}`)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.CATEGORY,
      path: `${PATHS.parametrization.category.index}`,
      component: <Categories />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.CATEGORY,
      path: `${PATHS.parametrization.category.new}`,
      component: (
        <NewCategory
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.category.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.CATEGORY,
      path: `${PATHS.parametrization.category.view(':id')}`,
      component: (
        <EditCategory
          readonly
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.category.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.CATEGORY,
      path: `${PATHS.parametrization.category.edit(':id')}`,
      component: (
        <EditCategory
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.category.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_CATEGORY,
      path: '/parametrizacao-categorias-vistoria',
      component: <CategoriaVistoria />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_CATEGORY,
      path: `${PATHS.parametrization.inspectionCategories.new}`,
      component: (
        <NewCategoriaVistoria
          returnToTable={() =>
            handleReturnToTable(
              `${PATHS.parametrization.inspectionCategories.index}`
            )
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_CATEGORY,
      path: `${PATHS.parametrization.inspectionCategories.view(':id')}`,
      component: (
        <EditCategoryInspection
          readonly
          returnToTable={() =>
            handleReturnToTable(
              `${PATHS.parametrization.inspectionCategories.index}`
            )
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_CATEGORY,
      path: `${PATHS.parametrization.inspectionCategories.edit(':id')}`,
      component: (
        <EditCategoryInspection
          returnToTable={() =>
            handleReturnToTable(
              `${PATHS.parametrization.inspectionCategories.index}`
            )
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.BREAKDOWN,
      path: `${PATHS.parametrization.breakdowns.index}`,
      component: <Breakdowns />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.BREAKDOWN,
      path: `${PATHS.parametrization.breakdowns.new}`,
      component: (
        <NewBreakdown
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.breakdowns.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.BREAKDOWN,
      path: `${PATHS.parametrization.breakdowns.view(':id')}`,
      component: (
        <EditBreakdown
          readonly
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.breakdowns.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.BREAKDOWN,
      path: `${PATHS.parametrization.breakdowns.edit(':id')}`,
      component: (
        <EditBreakdown
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.breakdowns.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.APPRAISAL_COMPANY,
      path: PATHS.parametrization.appraisalCompany.list,
      component: <AppraisalCompany />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.APPRAISAL_COMPANY,
      path: PATHS.parametrization.appraisalCompany.new,
      component: <NewCompany />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.APPRAISAL_COMPANY,
      path: PATHS.parametrization.appraisalCompany.view(':id'),
      component: <EditAppraisalCompany readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.APPRAISAL_COMPANY,
      path: PATHS.parametrization.appraisalCompany.edit(':id'),
      component: <EditAppraisalCompany />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SLA,
      path: `${PATHS.parametrization.sla.index}`,
      component: <SLA />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SLA,
      path: `${PATHS.parametrization.sla.new}`,
      component: (
        <NewSLA
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.sla.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.SLA,
      path: `${PATHS.parametrization.sla.view(':id')}`,
      component: (
        <EditSLA
          readonly
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.sla.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.SLA,
      path: `${PATHS.parametrization.sla.edit(':id')}`,
      component: (
        <EditSLA
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.sla.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PRODUCT,
      path: PATHS.parametrization.products.list,
      component: <Products />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PRODUCT,
      path: PATHS.parametrization.products.new,
      component: <NewProduct />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PRODUCT,
      path: PATHS.parametrization.products.view(':id'),
      component: <EditProduct readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PRODUCT,
      path: PATHS.parametrization.products.edit(':id'),
      component: <EditProduct />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SERVICE,
      path: PATHS.parametrization.services.list,
      component: <Services />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SERVICE,
      path: PATHS.parametrization.services.new,
      component: <NewService />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SERVICE,
      path: PATHS.parametrization.services.view(':id'),
      component: <EditServices readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.SERVICE,
      path: PATHS.parametrization.services.edit(':id'),
      component: <EditServices />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PROVIDER,
      path: PATHS.parametrization.providers.list,
      component: <Providers />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PROVIDER,
      path: PATHS.parametrization.providers.new,
      component: <NewProvider />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PROVIDER,
      path: PATHS.parametrization.providers.view(':id'),
      component: <EditProvider readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PROVIDER,
      path: PATHS.parametrization.providers.edit(':id'),
      component: <EditProvider />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ITEM_SERIES,
      path: PATHS.parametrization.serialItem.list,
      component: <SerialItem />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ITEM_SERIES,
      path: PATHS.parametrization.serialItem.new,
      component: <NewSerialItem />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ITEM_SERIES,
      path: PATHS.parametrization.serialItem.view(':id'),
      component: <EditSerialItem readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ITEM_SERIES,
      path: PATHS.parametrization.serialItem.edit(':id'),
      component: <EditSerialItem />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ACCESSORY,
      path: PATHS.parametrization.accessories.list,
      component: <Accessories />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_PARAMETRIZATION,
      path: PATHS.parametrization.inspection.index,
      component: <InspectionParametrization />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ACCESSORY,
      path: PATHS.parametrization.accessories.new,
      component: <NewAccessorie />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ACCESSORY,
      path: PATHS.parametrization.accessories.view(':id'),
      component: <EditAccessorie readonly />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.ACCESSORY,
      path: PATHS.parametrization.accessories.edit(':id'),
      component: <EditAccessorie />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.BRAND,
      path: `${PATHS.parametrization.brands.index}`,
      component: <Mark />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.BRAND,
      path: `${PATHS.parametrization.brands.new}`,
      component: (
        <NewMark
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.brands.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.BRAND,
      path: `${PATHS.parametrization.brands.view(':id')}`,
      component: (
        <EditMark
          readonly
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.brands.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.BRAND,
      path: `${PATHS.parametrization.brands.edit(':id')}`,
      component: (
        <EditMark
          returnToTable={() =>
            handleReturnToTable(`${PATHS.parametrization.brands.index}`)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.EVALUATION_TYPE_PARAMETRIZATION,
      path: PATHS.parametrization.evaluationType.index,
      component: <EvaluationType />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.EVALUATION_TYPE_PARAMETRIZATION,
      path: PATHS.parametrization.evaluationType.new,
      component: (
        <NewEvaluationType
          returnToTable={() =>
            navigate(PATHS.parametrization.evaluationType.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.EVALUATION_TYPE_PARAMETRIZATION,
      path: PATHS.parametrization.evaluationType.edit(':id'),
      component: (
        <EditEvaluationType
          returnToTable={() =>
            navigate(PATHS.parametrization.evaluationType.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_PHOTO_PARAMETRIZATION,
      path: PATHS.parametrization.inspectionPhotos.index,
      component: <InspectionPhotos />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_PHOTO_PARAMETRIZATION,
      path: PATHS.parametrization.inspectionPhotos.new,
      component: (
        <NewInspectionPhoto
          returnToTable={() =>
            navigate(PATHS.parametrization.inspectionPhotos.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.INSPECTION_PHOTO_PARAMETRIZATION,
      path: PATHS.parametrization.inspectionPhotos.edit(':id'),
      component: (
        <EditInspectionPhoto
          returnToTable={() =>
            navigate(PATHS.parametrization.inspectionPhotos.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_SECTION_PARAMETRIZATION,
      path: PATHS.parametrization.photoSections.index,
      component: <PhotoSections />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_SECTION_PARAMETRIZATION,
      path: PATHS.parametrization.photoSections.new,
      component: (
        <NewPhotoSection
          returnToTable={() =>
            navigate(PATHS.parametrization.photoSections.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_SECTION_PARAMETRIZATION,
      path: PATHS.parametrization.photoSections.edit(':id'),
      component: (
        <EditPhotoSection
          returnToTable={() =>
            navigate(PATHS.parametrization.photoSections.index)
          }
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_FLOW_PARAMETRIZATION,
      path: PATHS.parametrization.photoFlow.index,
      component: <PhotoFlow />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_FLOW_PARAMETRIZATION,
      path: PATHS.parametrization.photoFlow.new,
      component: (
        <NewPhotoFlow
          returnToTable={() => navigate(PATHS.parametrization.photoFlow.index)}
        />
      ),
      allowanonymous: false
    },
    {
      resource: PermissionResource.PHOTO_FLOW_PARAMETRIZATION,
      path: PATHS.parametrization.photoFlow.edit(':id'),
      component: (
        <EditPhotoFlow
          returnToTable={() => navigate(PATHS.parametrization.photoFlow.index)}
        />
      ),
      allowanonymous: false
    },

    {
      resource: PermissionResource.PHOTO_FLOW_PARAMETRIZATION,
      path: PATHS.parametrization.checklist.index,
      component: <Checklist />,
      allowanonymous: false
    },

    {
      resource: PermissionResource.INTEGRACOES,
      path: PATHS.integrations.index,
      component: <Integrations />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.FILE_MANAGER,
      path: PATHS.files.index,
      component: <Files />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.COST_CENTER,
      path: PATHS.currentActiveLocation.list,
      component: <AssetLocation />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.COST_CENTER,
      path: PATHS.currentActiveLocation.history(':id'),
      component: <AssetHistory />,
      allowanonymous: false
    },
    {
      resource: PermissionResource.DASHBOARD_MANAGEMENT_ANALYSIS,
      path: '/analises-gerenciais',
      component: <ManagementAnalysis />,
      allowanonymous: false,
      extraPermissions: [
        { resource: PermissionResource.MODEL, action: PermissionAction.READ }
      ]
    }
  ]

  function verifyRoutesData(
    path: any,
    component: any,
    resource?: any,
    allowanonymous?: boolean,
    action?: PermissionAction,
    extraPermissions?: PermissionCheck[]
  ) {
    const isPublicRoute = loginPath.some((route) => route.path === path)

    if (!isPublicRoute && isTokenExpired()) {
      localStorage.clear()
      return <Navigate to="/login" replace />
    }

    if (resource != null && allowanonymous === false) {
      const allowedResource = HAS_PERMISSION?.(resource, action)
      const allowedExtraPermissions = extraPermissions
        ? HAS_ALL_PERMISSIONS(extraPermissions)
        : true

      if (!allowedResource || !allowedExtraPermissions) return <Forbidden />
    }

    if (path.includes(PATHS.users.index)) {
      return (
        <ProfileProvider>
          <UsersProvider>{component}</UsersProvider>
        </ProfileProvider>
      )
    }
    if (path.includes('/parametrizacao')) {
      return <ParametrizationProvider>{component}</ParametrizationProvider>
    }

    if (path.includes('/vistorias')) {
      return (
        <PhotoProvider>
          <ParametrizationProvider>
            <PrecificationProvider>
              <StepperProvider>
                <InspectionProvider>{component}</InspectionProvider>
              </StepperProvider>
            </PrecificationProvider>
          </ParametrizationProvider>
        </PhotoProvider>
      )
    }

    if (path.includes('/precificacao')) {
      return (
        <PhotoProvider>
          <PrecificationProvider>
            <GraphysDataProvider>
              <InspectionProvider>{component}</InspectionProvider>
            </GraphysDataProvider>
          </PrecificationProvider>
        </PhotoProvider>
      )
    }

    if (path.includes('/preparacao')) {
      return (
        <ParametrizationProvider>
          <PreparationProvider>{component}</PreparationProvider>
        </ParametrizationProvider>
      )
    }

    if (path.includes('/dashboard')) {
      return <DashboardProvider>{component}</DashboardProvider>
    }

    if (path.includes('/profile')) {
      return <ProfileProvider>{component}</ProfileProvider>
    }

    if (path.includes('/analises-gerenciais')) {
      return (
        <ManagementAnalysisProvider>{component}</ManagementAnalysisProvider>
      )
    }

    if (path.includes('reembolso')) {
      return <ParametrizationProvider>{component}</ParametrizationProvider>
    }

    return <>{component}</>
  }

  return (
    <Routes>
      {systemPath.map((route, index) => {
        return (
          <Route
            key={index}
            path={route.path}
            element={
              <Template>
                <Menu />
                <CustomView maxHeight="100vh" overflow="auto">
                  <LoginProvider>
                    <DashboardHeader />
                    <CustomView pt="32px" pb="40px" pr="26px" pl="20px">
                      <GlobalInformationsProvider>
                        <Suspense
                          fallback={<CustomGraphy>Carregando..</CustomGraphy>}
                        >
                          <AssetLocationProvider>
                            <LoadingProvider>
                              {verifyRoutesData(
                                route.path,
                                route.component,
                                route.resource,
                                route.allowanonymous,
                                route?.action,
                                route?.extraPermissions
                              )}
                            </LoadingProvider>
                          </AssetLocationProvider>
                        </Suspense>
                      </GlobalInformationsProvider>
                    </CustomView>
                  </LoginProvider>
                </CustomView>
              </Template>
            }
          />
        )
      })}

      {loginPath.map((route, index) => {
        return (
          <Route
            key={index}
            path={route.path}
            element={
              <Suspense fallback={<CustomGraphy>Loading..</CustomGraphy>}>
                <LoadingProvider>
                  <LoginProvider>
                    <Login fixed={route.imgFixed}>{route.component}</Login>
                  </LoginProvider>
                </LoadingProvider>
              </Suspense>
            }
          />
        )
      })}
    </Routes>
  )
}

export default LogisticRoutes
